import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  useTheme,
} from "@mui/material";
import React, { useCallback, useRef, useState, useEffect, useMemo } from "react";
import SignatureCanvas from "react-signature-canvas";
import CloseIcon from "@mui/icons-material/Close";
import DrawIcon from "@mui/icons-material/Draw";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useResponsive } from "../../hooks/ResponsiveProvider";
import { useSelector } from "react-redux";
import CustomButton from "../@extended/CustomButton";

// Available cursive fonts for typed signature
const SIGNATURE_FONTS = [
  { name: 'Dancing Script', label: 'Dancing Script' },
  { name: 'Great Vibes', label: 'Great Vibes' },
  { name: 'Pacifico', label: 'Pacifico' },
  { name: 'Satisfy', label: 'Satisfy' },
  { name: 'Allura', label: 'Allura' },
  { name: 'Sacramento', label: 'Sacramento' },
];

// Google Fonts link for cursive fonts
const GOOGLE_FONTS_LINK = "https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Pacifico&family=Satisfy&family=Allura&family=Sacramento&display=swap";

// Helper text for each signature mode
const MODE_HELPER_TEXT = {
  draw: 'Draw your signature using your mouse or finger',
  type: 'Type your name and select a font style',
  upload: 'Upload an image of your signature (PNG or JPEG, max 2MB)',
};

const SignatureBox = (props) => {
  const { 
    onCloseModal, 
    open, 
    handleClose, 
    saveSign, 
    bulkApprove, 
    type = 'Save',
    savedSignatureData = null,
    userSignaturePreferences = null,
    onUpdatePreferences = null,
  } = props;
  
  const { isSmallScreen } = useResponsive();
  const theme = useTheme();
  const userData = useSelector((state) => state.auth.userData);
  
  // Get user's first and last name for typed signature default
  const defaultTypedName = useMemo(() => {
    const firstName = userData?.firstName || '';
    const lastName = userData?.lastName || '';
    return `${firstName} ${lastName}`.trim();
  }, [userData]);

  // State for signature mode: 'draw', 'type', or 'upload'
  const [signatureMode, setSignatureMode] = useState(
    userSignaturePreferences?.signaturePreference || 'draw'
  );
  
  // State for typed signature
  const [typedName, setTypedName] = useState(defaultTypedName);
  const [selectedFont, setSelectedFont] = useState(
    userSignaturePreferences?.signatureFont || 'Dancing Script'
  );
  
  // State for uploaded signature
  const [uploadedSignature, setUploadedSignature] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // State for error/validation feedback
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState(''); // Capture error message for snackbar
  
  // State for clear confirmation
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  
  // State to track if canvas has content (for draw mode reactivity)
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  
  // Track initial preferences to detect changes
  const initialPreferencesRef = useRef({
    mode: userSignaturePreferences?.signaturePreference || 'draw',
    font: userSignaturePreferences?.signatureFont || 'Dancing Script',
  });
  
  // Ref for draw signature canvas
  const padRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load Google Fonts when component mounts
  useEffect(() => {
    const existingLink = document.querySelector(`link[href="${GOOGLE_FONTS_LINK}"]`);
    if (!existingLink) {
      const link = document.createElement('link');
      link.href = GOOGLE_FONTS_LINK;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  }, []);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setTypedName(defaultTypedName);
      setUploadedSignature(null);
      setUploadPreview(savedSignatureData || null);
      setError(null);
      setShowClearConfirm(false);
      setHasDrawnSignature(false);
      // Clear the canvas when dialog opens
      if (padRef.current) {
        padRef.current.clear();
      }
      const initialMode = userSignaturePreferences?.signaturePreference || 'draw';
      const initialFont = userSignaturePreferences?.signatureFont || 'Dancing Script';
      setSignatureMode(initialMode);
      setSelectedFont(initialFont);
      // Update initial preferences ref when dialog opens
      initialPreferencesRef.current = {
        mode: initialMode,
        font: initialFont,
      };
    }
  }, [open, defaultTypedName, savedSignatureData, userSignaturePreferences]);

  // Clear error when mode changes
  useEffect(() => {
    setError(null);
  }, [signatureMode]);

  // Check if preferences have changed from initial values
  const hasPreferencesChanged = useCallback(() => {
    return (
      signatureMode !== initialPreferencesRef.current.mode ||
      selectedFont !== initialPreferencesRef.current.font
    );
  }, [signatureMode, selectedFont]);

  // Check if signature has content
  const hasSignatureContent = useCallback(() => {
    if (signatureMode === 'draw') {
      // Use both state and ref check for accuracy
      return hasDrawnSignature || (padRef.current && !padRef.current.isEmpty());
    } else if (signatureMode === 'type') {
      return typedName.trim().length > 0;
    } else if (signatureMode === 'upload') {
      return !!uploadPreview;
    }
    return false;
  }, [signatureMode, typedName, uploadPreview, hasDrawnSignature]);

  // Generate typed signature as canvas data URL
  const generateTypedSignature = useCallback(() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size - use responsive width
    const canvasWidth = isSmallScreen ? 280 : 500;
    const canvasHeight = isSmallScreen ? 100 : 150;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    
    // Clear canvas with transparent background
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set font and style
    const fontSize = isSmallScreen ? 32 : 48;
    ctx.font = `${fontSize}px "${selectedFont}"`;
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw the typed name
    ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
    
    return canvas.toDataURL('image/png');
  }, [typedName, selectedFont, isSmallScreen]);

  // Handle file upload with inline error feedback
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.match('image/(png|jpeg|jpg)')) {
        const errorMsg = 'Please upload a PNG or JPEG image';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        const errorMsg = 'File size must be less than 2MB';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        return;
      }
      
      setError(null);
      setUploadedSignature(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Confirmed clear action
  const handleClearConfirmed = useCallback(() => {
    if (signatureMode === 'draw') {
      padRef.current?.clear();
      setHasDrawnSignature(false);
    } else if (signatureMode === 'type') {
      setTypedName('');
    } else if (signatureMode === 'upload') {
      setUploadedSignature(null);
      setUploadPreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    setShowClearConfirm(false);
    setError(null);
  }, [signatureMode]);

  // Handle clear with confirmation
  const handleClearClick = useCallback(() => {
    if (hasSignatureContent()) {
      setShowClearConfirm(true);
    } else {
      // Nothing to clear, just reset
      handleClearConfirmed();
    }
  }, [hasSignatureContent, handleClearConfirmed]);

  // Cancel clear
  const handleClearCancel = useCallback(() => {
    setShowClearConfirm(false);
  }, []);

  // Close snackbar
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  // Handle save with validation
  const handleSave = useCallback(async () => {
    // Validate signature exists
    if (!hasSignatureContent()) {
      const errorMessages = {
        draw: 'Please draw your signature before saving',
        type: 'Please type your name before saving',
        upload: 'Please upload a signature image before saving',
      };
      setError(errorMessages[signatureMode]);
      return;
    }

    setIsProcessing(true);
    setError(null);
    
    try {
      // Step 1: Update preferences if changed
      if (hasPreferencesChanged() && onUpdatePreferences) {
        try {
          await onUpdatePreferences({
            signaturePreference: signatureMode,
            signatureFont: selectedFont,
          });
          // Update the initial preferences ref after successful save
          initialPreferencesRef.current = {
            mode: signatureMode,
            font: selectedFont,
          };
        } catch (error) {
          console.error('Failed to update signature preferences:', error);
          // Continue with signature save even if preferences update fails
        }
      }

      // Step 2: Save signature based on mode
      if (signatureMode === 'draw') {
        const canvas = padRef.current.getTrimmedCanvas();
        const data = canvas.toDataURL();
        await saveSign(data, { mode: 'draw' });
      } else if (signatureMode === 'type') {
        const signatureData = generateTypedSignature();
        await saveSign(signatureData, { mode: 'type', font: selectedFont });
      } else if (signatureMode === 'upload') {
        // All signatures (including upload) go through e-consent service
        await saveSign(uploadPreview, { mode: 'upload' });
      }
    } catch (saveError) {
      console.error('Error saving signature:', saveError);
      setError('Failed to save signature. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [
    signatureMode, 
    selectedFont, 
    uploadPreview, 
    hasPreferencesChanged, 
    hasSignatureContent,
    onUpdatePreferences, 
    saveSign, 
    generateTypedSignature,
  ]);

  // Get button label
  const getButtonLabel = () => {
    if (bulkApprove === true) return "Submit & Approve";
    if (type === 'COMPLETED') return 'Save & Complete';
    if (type === 'APPROVED') return 'Save & Approve';
    return 'Save';
  };

  // Get dialog title based on action type
  const getDialogTitle = () => {
    if (bulkApprove === true) return "Sign to Submit & Approve";
    if (type === 'COMPLETED') return 'Sign to Complete';
    if (type === 'APPROVED') return 'Sign to Approve';
    return 'Sign Here';
  };

  // Render draw mode content
  const renderDrawMode = () => (
    <Paper 
      sx={{ 
        width: '100%',
        maxWidth: isSmallScreen ? 300 : 500,
        marginTop: 2,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          borderRadius: 3,
          boxShadow: "1px 1px 4px 0 rgba(0, 0, 0, 0.2), 0 1px 8px 0 rgba(0, 0, 0, 0.10)",
          overflow: 'hidden',
        }}
      >
        <SignatureCanvas
          ref={padRef}
          velocityFilterWeight={0.7}
          minWidth={0.5}
          maxWidth={2.5}
          throttle={16}
          onEnd={() => setHasDrawnSignature(true)}
          canvasProps={{
            width: isSmallScreen ? 280 : 500,
            height: isSmallScreen ? 150 : 200,
            style: {
              width: '100%',
              height: isSmallScreen ? 150 : 200,
              touchAction: 'none',
            },
          }}
        />
      </Box>
    </Paper>
  );

  // Render type mode content
  const renderTypeMode = () => (
    <Box 
      sx={{ 
        width: '100%',
        maxWidth: isSmallScreen ? 300 : 500,
        marginTop: 2,
        mx: 'auto',
      }}
    >
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="signature-font-label">Font Style</InputLabel>
        <Select
          labelId="signature-font-label"
          value={selectedFont}
          label="Font Style"
          onChange={(e) => setSelectedFont(e.target.value)}
        >
          {SIGNATURE_FONTS.map((font) => (
            <MenuItem 
              key={font.name} 
              value={font.name}
              sx={{ fontFamily: font.name }}
            >
              {font.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      <TextField
        fullWidth
        label="Type your name"
        value={typedName}
        onChange={(e) => setTypedName(e.target.value)}
        sx={{ mb: 2 }}
        placeholder="Enter your full name"
        error={!!error && signatureMode === 'type'}
        helperText={error && signatureMode === 'type' ? error : ''}
      />
      
      {/* Preview of typed signature */}
      <Paper
        sx={{
          p: 2,
          borderRadius: 3,
          boxShadow: "1px 1px 4px 0 rgba(0, 0, 0, 0.2), 0 1px 8px 0 rgba(0, 0, 0, 0.10)",
          minHeight: isSmallScreen ? 100 : 150,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fff',
        }}
      >
        <Typography
          sx={{
            fontFamily: selectedFont,
            fontSize: isSmallScreen ? 32 : 48,
            color: typedName ? '#000' : theme.palette.text.disabled,
            textAlign: 'center',
            fontStyle: typedName ? 'normal' : 'italic',
          }}
        >
          {typedName || 'Your signature preview'}
        </Typography>
      </Paper>
    </Box>
  );

  // Render upload mode content
  const renderUploadMode = () => (
    <Box 
      sx={{ 
        width: '100%',
        maxWidth: isSmallScreen ? 300 : 500,
        marginTop: 2,
        mx: 'auto',
      }}
    >
      <input
        type="file"
        accept="image/png,image/jpeg,image/jpg"
        onChange={handleFileUpload}
        ref={fileInputRef}
        style={{ display: 'none' }}
        id="signature-upload-input"
      />
      
      <Box
        sx={{
          border: `2px dashed ${error && signatureMode === 'upload' ? theme.palette.error.main : theme.palette.primary.main}`,
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          minHeight: isSmallScreen ? 150 : 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: uploadPreview ? '#fff' : theme.palette.grey[50],
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
            borderColor: theme.palette.primary.dark,
          },
          '&:active': {
            transform: 'scale(0.99)',
          },
        }}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            fileInputRef.current?.click();
          }
        }}
        aria-label="Upload signature image"
      >
        {uploadPreview ? (
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            <img
              src={uploadPreview}
              alt="Uploaded signature preview"
              style={{
                maxWidth: '100%',
                maxHeight: isSmallScreen ? 100 : 150,
                objectFit: 'contain',
              }}
            />
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              Click to change
            </Typography>
          </Box>
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }} />
            <Typography variant="body1" color="textSecondary">
              Click to upload signature image
            </Typography>
            <Typography variant="caption" color="textSecondary">
              PNG or JPEG, max 2MB
            </Typography>
          </>
        )}
      </Box>
      
      {/* Show error below upload box */}
      {error && signatureMode === 'upload' && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
    <Dialog
        maxWidth="sm"
        fullWidth
        fullScreen={isSmallScreen}
        PaperProps={{
          sx: {
            minHeight: isSmallScreen ? '100%' : '50%',
            maxHeight: isSmallScreen ? '100%' : '90vh',
          },
        }}
      scroll="paper"
      open={open}
      onClose={onCloseModal}
    >
        <DialogTitle sx={{ pr: 6 }}>{getDialogTitle()}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
            position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Signature Mode Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, width: '100%' }}>
          <ToggleButtonGroup
            value={signatureMode}
            exclusive
            onChange={(e, newMode) => {
              if (newMode !== null) {
                setSignatureMode(newMode);
              }
            }}
            aria-label="signature mode"
              orientation={isSmallScreen ? 'vertical' : 'horizontal'}
              fullWidth={isSmallScreen}
              sx={{
                '& .MuiToggleButton-root': {
                  px: isSmallScreen ? 2 : 3,
                  py: 1,
                },
              }}
          >
            <ToggleButton value="draw" aria-label="draw signature">
              <DrawIcon sx={{ mr: 0.5 }} />
              Draw
            </ToggleButton>
            <ToggleButton value="type" aria-label="type signature">
              <KeyboardIcon sx={{ mr: 0.5 }} />
              Type
            </ToggleButton>
            <ToggleButton value="upload" aria-label="upload signature">
              <CloudUploadIcon sx={{ mr: 0.5 }} />
              Upload
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

          {/* Helper text for current mode */}
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              mb: 1,
              textAlign: 'center',
              maxWidth: isSmallScreen ? 280 : 500,
            }}
          >
            {MODE_HELPER_TEXT[signatureMode]}
          </Typography>

          {/* Show validation error for draw mode */}
          {error && signatureMode === 'draw' && (
            <Alert severity="error" sx={{ mb: 1, width: '100%', maxWidth: isSmallScreen ? 300 : 500 }}>
              {error}
            </Alert>
          )}
        
        {/* Render content based on mode */}
        {signatureMode === 'draw' && renderDrawMode()}
        {signatureMode === 'type' && renderTypeMode()}
        {signatureMode === 'upload' && renderUploadMode()}
      </DialogContent>
      
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {showClearConfirm ? (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ flex: 1 }}>
                Clear signature?
              </Typography>
              <Button onClick={handleClearCancel} size="small">
                Cancel
              </Button>
              <Button onClick={handleClearConfirmed} color="error" size="small">
                Yes, Clear
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleClearClick} disabled={isProcessing}>
          Clear
        </Button>
              <Box>
                <CustomButton onClick={handleSave} disabled={isProcessing} variant="contained" size="small">
                  {isProcessing ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {getButtonLabel()}
                </CustomButton>
              </Box>
            </>
          )}
      </DialogActions>
    </Dialog>

      {/* Snackbar for file upload errors */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SignatureBox;
